// Signup-Page. Pre-Auth. Setzt 14-Tage-Trial direkt (startTrial=true).

import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Sparkles, Mail, Lock, User } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';

export function SignupPage() {
  const { signUp, isAuthenticated, error, setError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/markt" replace />;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !password || !nickname) {
      setError('Bitte alle Felder ausfüllen.');
      return;
    }
    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    setSubmitting(true);
    try {
      await signUp(email, password, nickname, true);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative grid min-h-dvh place-items-center px-4">
      <div className="aurora">
        <div className="aurora__blob aurora__blob--1"></div>
        <div className="aurora__blob aurora__blob--2"></div>
        <div className="aurora__blob aurora__blob--3"></div>
      </div>
      <div className="aurora-grid"></div>
      <div className="aurora-vignette"></div>

      <form
        onSubmit={handleSubmit}
        className="glass-strong relative z-10 w-full max-w-sm rounded-2xl p-6 text-neutral-100"
      >
        <div className="mb-5 flex flex-col items-center text-center">
          <img
            src="/eve-satoshi.png"
            alt="EVE mit Satoshi"
            className="eve-avatar-pulse mb-3 h-16 w-16 rounded-full object-cover ring-2 ring-amber-500/50"
          />
          <h1 className="text-xl font-semibold">Account erstellen</h1>
          <p className="mt-1 text-sm text-neutral-400">14 Tage Trial inklusive.</p>
        </div>

        {done ? (
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
            Account erstellt. Du kannst dich jetzt{' '}
            <Link to="/login" className="font-medium underline">
              anmelden
            </Link>
            .
          </div>
        ) : (
          <>
            <label className="mb-3 block text-sm">
              <span className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">
                Nickname
              </span>
              <span className="glass flex items-center gap-2 rounded-lg px-3 py-2">
                <User className="size-4 text-neutral-500" />
                <input
                  type="text"
                  required
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-500"
                  placeholder="Doc"
                />
              </span>
            </label>

            <label className="mb-3 block text-sm">
              <span className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">
                E-Mail
              </span>
              <span className="glass flex items-center gap-2 rounded-lg px-3 py-2">
                <Mail className="size-4 text-neutral-500" />
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-500"
                  placeholder="doc@upro.de"
                />
              </span>
            </label>

            <label className="mb-4 block text-sm">
              <span className="mb-1 block text-xs uppercase tracking-wider text-neutral-500">
                Passwort
              </span>
              <span className="glass flex items-center gap-2 rounded-lg px-3 py-2">
                <Lock className="size-4 text-neutral-500" />
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-neutral-100 outline-none placeholder:text-neutral-500"
                  placeholder="min. 8 Zeichen"
                />
              </span>
            </label>

            {error ? (
              <p className="mb-3 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-neutral-950 shadow-[0_0_20px_rgba(251,191,36,0.3)] transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <span className="tabular">Wird erstellt…</span>
              ) : (
                <>
                  <Sparkles className="size-4" />
                  <span>Account erstellen</span>
                </>
              )}
            </button>

            <div className="mt-4 text-center text-xs text-neutral-400">
              Schon einen Account?{' '}
              <Link to="/login" className="text-amber-300 hover:underline">
                Anmelden
              </Link>
            </div>
          </>
        )}
      </form>
    </main>
  );
}
